import React, { useState, useEffect } from 'react';
import { Star, Clock, Users, Search, Filter, MapPin, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { getTutors } from '../services/api';

export const FindTutor = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [teachingModeFilter, setTeachingModeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [distanceFilter, setDistanceFilter] = useState('all');

  // User location for distance calculation
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationReady, setLocationReady] = useState(false);

  // Get user's location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
          setLocationReady(true);
        },
        (error) => {
          console.log('Location access denied or unavailable');
          setLocationLoading(false);
          setLocationReady(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    } else {
      setLocationLoading(false);
      setLocationReady(true);
    }
  };

  useEffect(() => {
    fetchTutors();
  }, [searchQuery, selectedSubject, sortBy, locationReady]);

  const fetchTutors = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery,
        subject: selectedSubject,
        sort_by: sortBy
      };

      // Add user location for distance calculation if available
      if (userLocation) {
        params.user_lat = userLocation.lat;
        params.user_lng = userLocation.lng;
      }

      const data = await getTutors(params);
      setTutors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side advanced filtering
  const filteredTutors = tutors.filter(tutor => {
    // Distance filter
    if (distanceFilter !== 'all' && userLocation) {
      const maxDist = parseInt(distanceFilter);
      const tutorDist = tutor.distance_km;
      if (tutorDist === null || tutorDist === undefined || tutorDist > maxDist) return false;
    }

    // City/Location filter - check both city and location fields
    if (cityFilter) {
      const cityLower = cityFilter.toLowerCase();
      const hasCity = tutor.city?.toLowerCase().includes(cityLower);
      const hasLocation = tutor.location?.toLowerCase().includes(cityLower);
      const hasAddress = tutor.address?.toLowerCase().includes(cityLower);
      if (!hasCity && !hasLocation && !hasAddress) return false;
    }

    // Price range filter
    if (priceRange.min && tutor.hourly_rate < parseInt(priceRange.min)) return false;
    if (priceRange.max && tutor.hourly_rate > parseInt(priceRange.max)) return false;

    // Experience filter
    if (experienceFilter !== 'all') {
      const exp = tutor.experience_years || 0;
      if (experienceFilter === '0-2' && (exp < 0 || exp > 2)) return false;
      if (experienceFilter === '3-5' && (exp < 3 || exp > 5)) return false;
      if (experienceFilter === '5+' && exp < 5) return false;
    }

    // Teaching mode filter
    if (teachingModeFilter !== 'all') {
      const modes = tutor.teaching_mode || [];
      if (!modes.includes(teachingModeFilter)) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-3">Find Your Perfect Tutor</h1>
          <p className="text-lg text-gray-600">Browse through our verified tutors and book a session</p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8 border border-gray-100">
          <div className="grid md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search tutors or subjects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="tutor-search-input"
              />
            </div>

            {/* City Filter */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Filter by city..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="pl-10"
                data-testid="city-filter-input"
              />
            </div>

            {/* Subject Filter */}
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger data-testid="subject-filter">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                <SelectItem value="Mathematics">Mathematics</SelectItem>
                <SelectItem value="Physics">Physics</SelectItem>
                <SelectItem value="Chemistry">Chemistry</SelectItem>
                <SelectItem value="Biology">Biology</SelectItem>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Computer Science">Computer Science</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location Status */}
          {!userLocation && !locationLoading && (
            <div className="flex items-center gap-2 text-sm text-amber-600 mb-4">
              <Navigation className="h-4 w-4" />
              <span>Enable location to see distances and sort by nearest</span>
              <Button variant="link" size="sm" onClick={getUserLocation} className="text-blue-600 p-0 h-auto">
                Enable
              </Button>
            </div>
          )}

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-fit"
            data-testid="advanced-filters-toggle"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
          </Button>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="grid md:grid-cols-6 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Sort By</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-by-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="experience">Most Experienced</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    {userLocation && <SelectItem value="distance">Nearest First</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              {userLocation && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Max Distance</label>
                  <Select value={distanceFilter} onValueChange={setDistanceFilter}>
                    <SelectTrigger data-testid="distance-filter">
                      <SelectValue placeholder="Any Distance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Distance</SelectItem>
                      <SelectItem value="5">Within 5 km</SelectItem>
                      <SelectItem value="10">Within 10 km</SelectItem>
                      <SelectItem value="25">Within 25 km</SelectItem>
                      <SelectItem value="50">Within 50 km</SelectItem>
                      <SelectItem value="100">Within 100 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Experience</label>
                <Select value={experienceFilter} onValueChange={setExperienceFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Experience</SelectItem>
                    <SelectItem value="0-2">0-2 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="5+">5+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Teaching Mode</label>
                <Select value={teachingModeFilter} onValueChange={setTeachingModeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modes</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                    <SelectItem value="Offline">Offline</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Min Price (₹/hour)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Max Price (₹/hour)</label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold">{filteredTutors.length}</span> tutors
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading tutors...</p>
          </div>
        )}

        {/* Tutors Grid */}
        {!loading && filteredTutors.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutors.map((tutor) => (
              <div
                key={tutor.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all border border-gray-100 overflow-hidden group cursor-pointer"
                onClick={(e) => {
                  // Prevent navigation if clicking on the button itself
                  if (!e.target.closest('button')) {
                    navigate(`/tutors/${tutor.id}`);
                  }
                }}
                data-testid={`tutor-card-${tutor.id}`}
              >
                {/* Tutor Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={tutor.image}
                    alt={tutor.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full flex items-center space-x-1 shadow-lg">
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-bold text-gray-900">{tutor.rating}</span>
                  </div>
                </div>

                {/* Tutor Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {tutor.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-2">{tutor.subject}</p>

                  {/* Location with Distance */}
                  {(tutor.city || tutor.location) && (
                    <div className="flex items-center text-gray-500 text-sm mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{tutor.city || tutor.location}</span>
                      {tutor.distance_display && (
                        <span className="ml-2 text-blue-600 font-medium">• {tutor.distance_display} away</span>
                      )}
                    </div>
                  )}

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{tutor.bio}</p>

                  {/* Stats */}
                  <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{tutor.experience}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{tutor.students} students</span>
                    </div>
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">₹{tutor.hourly_rate}</p>
                      <p className="text-xs text-gray-500">per hour</p>
                    </div>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`view-profile-${tutor.id}`}
                      onClick={() => navigate(`/tutors/${tutor.id}`)}
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && filteredTutors.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No tutors found matching your criteria.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearchQuery('');
                setSelectedSubject('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
